import React, { useState } from 'react';
import { format } from 'date-fns';
import { Button, Modal, ModalContent } from '../common';
import { Heading, Text } from '../common/Typography';
import { getPlayerNameColor } from '../../utils/playerUtils';
import { softDeleteGuest } from '../../services/guestService';

const DeleteGuestModal = ({ guest, clubId, currentUser, onConfirm, onCancel }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      
      await softDeleteGuest(guest.id, currentUser.id, clubId);
      onConfirm(guest);
    } catch (err) {
      console.error('Error deleting guest:', err);
      setError(err.message || 'Failed to delete guest');
    } finally {
      setDeleting(false);
    }
  };

  const hasGamesPlayed = (guest.totalGamesPlayed || 0) > 0;

  return (
    <Modal isOpen onClose={onCancel} closeOnBackdropClick={!deleting}>
      <ModalContent size="medium" className="overflow-y-auto">
          {/* Header */}
          <div className="mb-6">
            <Heading variant="h3" className="mb-2 text-danger-dark">
              Delete Guest Player
            </Heading>
            <Text className="text-text-secondary">
              This action will mark the guest for deletion. They will be permanently removed after 30 days.
            </Text>
          </div>

          {/* Guest Information */}
          <div className="mb-6">
            <div className="rounded-lg border border-border-medium bg-surface-subtle p-4">
              <div className="mb-3 flex items-center space-x-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-info-light">
                  <span className="text-lg font-medium text-info-dark">
                    {guest.first_name?.[0]}{guest.last_name?.[0]}
                  </span>
                </div>
                <div className="min-w-0">
                  <Text className={`font-medium text-text-primary ${getPlayerNameColor(guest.gender)}`}>
                    {guest.first_name} {guest.last_name}
                  </Text>
                  <Text className="text-sm capitalize text-text-muted">
                    {guest.gender}
                  </Text>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Text className="font-medium text-text-primary">Email Status:</Text>
                  <Text
                    className={
                      guest.guest_invited_email ? 'text-success-dark' : 'text-warning-dark'
                    }
                  >
                    {guest.guest_invited_email ? '✅ Email Assigned' : '⏳ No Email'}
                  </Text>
                </div>
                <div>
                  <Text className="font-medium text-text-primary">Created:</Text>
                  <Text className="text-text-secondary">
                    {format(new Date(guest.created_at), 'MMM dd, yyyy')}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Summary */}
          <div className="mb-6">
            <Heading variant="h4" className="mb-3 text-text-primary">
              Activity Summary
            </Heading>
            <div className="rounded-lg border border-border-medium bg-surface-subtle p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center rounded-full bg-info-light px-2 py-1 text-xs font-medium text-info-dark">
                    {guest.totalMixedGames || 0}
                  </span>
                  <Text className="text-text-secondary">Mixed Games</Text>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center rounded-full bg-success-light px-2 py-1 text-xs font-medium text-success-dark">
                    {guest.totalLevelsGames || 0}
                  </span>
                  <Text className="text-text-secondary">Levels Games</Text>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center rounded-full bg-female-light px-2 py-1 text-xs font-medium text-female">
                    {guest.totalSessions || 0}
                  </span>
                  <Text className="text-text-secondary">Sessions Attended</Text>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center rounded-full border border-border-medium bg-surface-elevated px-2 py-1 text-xs font-medium text-text-primary">
                    {guest.totalGamesPlayed || 0}
                  </span>
                  <Text className="text-text-secondary">Total Games</Text>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          {hasGamesPlayed && (
            <div className="mb-6">
              <div className="rounded-lg border border-warning/30 bg-warning-light p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <span className="text-lg text-warning-dark">⚠️</span>
                  </div>
                  <div>
                    <Text className="mb-1 font-medium text-warning-dark">
                      Guest Has Played Games
                    </Text>
                    <Text className="text-sm text-text-secondary">
                      This guest has played {guest.totalGamesPlayed} games across {guest.totalSessions} sessions.
                      Deleting will preserve game history but remove player access.
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Checkbox */}
          <div className="mb-6">
            <label className="flex cursor-pointer items-start space-x-3">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border-medium bg-surface text-primary focus:ring-2 focus:ring-primary"
              />
              <Text className="text-sm text-text-secondary">
                I understand this action can be undone within 30 days. The guest will be permanently deleted after the grace period.
              </Text>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4">
              <div className="rounded-lg border border-danger/30 bg-danger-light p-3">
                <Text className="text-sm text-danger-dark">{error}</Text>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button onClick={onCancel} variant="secondary" disabled={deleting} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="danger"
              disabled={!confirmed || deleting}
              loading={deleting}
              className="w-full sm:w-auto"
            >
              {deleting ? 'Deleting...' : 'Delete Guest'}
            </Button>
          </div>
      </ModalContent>
    </Modal>
  );
};

export default DeleteGuestModal;
